import React, { useEffect, useRef, useState } from 'react';
import { Collapse } from 'react-collapse';
import styled from 'styled-components';
import {
  Title,
  Card,
  Button,
  Spacer,
  mq,
  generateNameHash
} from '@kvdbil/components';
import { useTranslation } from '~/Locale';
import { TitleWithTextRecord } from '~/config/generated/graphql';
import { isEmptyDocument } from 'datocms-structured-text-utils';
import StructuredText from '~/App/components/DatoCMS/StructuredText';
import { DatoTitleTag } from '~/App/shared/interfaces/datoCMS';
import { useWindowSize } from '../hooks/useWindowSize';

const Content = styled.div`
  padding: 1rem 2rem;

  ${mq(null, 'mobileL')} {
    padding: 2rem 1rem;
  }
`;

const ExpandableText = styled.div<{
  $isExpanded: boolean;
  $maxHeight?: number;
}>`
  position: relative;
  overflow: hidden;
  max-height: ${({ $isExpanded, $maxHeight = 68 }) =>
    $isExpanded ? '100%' : `${$maxHeight}px`};

  a {
    font-weight: ${({ theme }) => theme.typography.fontBaseBoldWeight};
  }

  * {
    margin-top: 1.38rem;
    &:first-child {
      margin-top: 0;
    }
  }

  noscript:nth-child(1) + * {
    margin-top: 0;
  }
`;

const ExpandWrapper = styled.div`
  display: flex;
  justify-content: center;
  position: relative;
  transition: all 0.2 ease;
  padding-top: 1rem;
`;

interface Props {
  content: TitleWithTextRecord;
  defaultExpandedState?: boolean;
}

const SEOInfo = ({ content, defaultExpandedState = false }: Props) => {
  const { width } = useWindowSize();
  const [expanded, setExpanded] = useState(defaultExpandedState);
  const [isExpandable, setIsExpandable] = useState(false);
  const [firstElemHeight, setFirstElemHeight] = useState<number | undefined>();
  const expandableTextRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const title = content?.title?.[0];
  const text = content?.text;
  const idRef = useRef(generateNameHash('seo-info-content'));

  useEffect(() => {
    if (expandableTextRef.current?.firstChild) {
      const parentElem = expandableTextRef.current
        ?.firstChild as HTMLDivElement;
      const firstElem = parentElem?.firstChild as HTMLParagraphElement;

      if (parentElem?.childElementCount > 1) {
        setIsExpandable(true);
      } else {
        setExpanded(true);
      }
      firstElem?.offsetHeight > 0 &&
        setFirstElemHeight(firstElem?.offsetHeight);
    }
  }, [width]);

  if (!title?.text && isEmptyDocument(text)) {
    return null;
  }

  return (
    <Card>
      <Content>
        <Title as={(title?.tag ?? 'h3') as DatoTitleTag}>{title?.text}</Title>
        <Spacer height={0.5} />
        {!isEmptyDocument(text) && (
          <Collapse isOpened>
            <ExpandableText
              id={isExpandable ? idRef.current : undefined}
              as="div"
              $maxHeight={firstElemHeight}
              $isExpanded={expanded}
              ref={expandableTextRef}
            >
              <StructuredText data={text} />
            </ExpandableText>
          </Collapse>
        )}

        {isExpandable && (
          <ExpandWrapper>
            <Button
              onClick={() => setExpanded(prev => !prev)}
              size="regular"
              variant="flat"
              color="info"
              colorShade="dark"
              aria-expanded={expanded}
              aria-controls={idRef.current}
            >
              {expanded ? (
                <span>{t('Minimize')} </span>
              ) : (
                <span>{t('Show more')} </span>
              )}
            </Button>
          </ExpandWrapper>
        )}
      </Content>
    </Card>
  );
};

export default SEOInfo;
